<template>
  <div class="dmca_form">
    <h2>Intellectual Property Claim</h2>
    <p>
      Please use the following form to report content that you believe violates
      or infringes your copyright. If you are not sure if copyright laws protect
      the material, we suggest that you first contact a lawyer.
    </p>
    <div class="success">
      Thanks for contacting us. We'll get back to you as soon as possible.
    </div>
    <div class="form_info">
      <h3>Your Contact Information</h3>
      <label>Your name</label>
      <p>
        Note that your contact information may be provided to the person that
        posted the content you are reporting.
      </p>
      <div class="form_item" :class="{ had_input: payload.name }">
        <input
          type="text"
          name="name"
          id="name"
          maxlength="1000"
          @blur="required"
          v-model="payload.name"
        />
        <label for="">Please enter your full, legal name</label>
        <div class="error" :class="{ required: error.name }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <div class="form_item" :class="{ had_input: payload.email }">
        <input
          type="text"
          name="email"
          id="email"
          maxlength="1000"
          @blur="required"
          v-model="payload.email"
        />
        <label for="">Email</label>
        <div class="error" :class="{ required: error.email }">
          Must be a valid email address
        </div>
      </div>
      <div class="form_item" :class="{ had_input: payload.company }">
        <input
          type="text"
          name="company"
          maxlength="1000"
          id="company"
          v-model="payload.company"
        />
        <label for="">Company</label>
      </div>
      <label for="">Address</label>
      <div class="form_item" :class="{ had_input: payload.address }">
        <textarea
          placeholder="Address"
          name="address"
          id="address"
          maxlength="1000"
          @blur="required"
          v-model="payload.address"
        ></textarea>
        <div class="error" :class="{ required: error.address }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <div class="form_item" :class="{ had_input: payload.phone }">
        <input
          type="number"
          maxlength="1000"
          name="phone"
          id="phone"
          @blur="required"
          v-model="payload.phone"
        />
        <label for="">Phone</label>
        <div class="error" :class="{ required: error.phone }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <label>Copyright owner</label>
      <div class="form_radio">
        <label for="copyright_owner">
          <input
            type="radio"
            v-model="payload.copyright_owner"
            name="copyright_owner"
            id="copyright_owner"
            :value="true"
          />
          <span class="dot" :class="{ active: payload.copyright_owner }"></span>
          <span>I am the copyright owner</span>
        </label>
        <label for="authorized_owner">
          <input
            type="radio"
            v-model="payload.copyright_owner"
            name="authorized_owner"
            id="authorized_owner"
            :value="false"
          />
          <span
            class="dot"
            :class="{ active: !payload.copyright_owner }"
          ></span>
          <span>I am an authorized representative of the copyright owner</span>
        </label>
      </div>
    </div>
    <div class="form_info">
      <h3>Content you want to report</h3>
      <p>
        Please provide link(s) to the content you believe violates or infringes
        your copyright on this store.
      </p>
      <label for=""> Links to the allegedly infringing work </label>
      <p>
        Ensure you have provided a comprehensive list to each individual page
        with the content you would like removed and not just the main shop URL.
        Up to 100 links may be included in the same notice.
      </p>
      <div class="form_item" :class="{ had_input: payload.infringing_address }">
        <input
          type="text"
          name="infringing_address"
          maxlength="1000"
          id="infringing_address"
          @blur="required"
          v-model="payload.infringing_address"
        />
        <label for="">Links to the allegedly infringing work</label>
        <div class="error" :class="{ required: error.infringing_address }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <div
        v-for="(item, index) in link_infringing_list"
        :key="index + 'link_infringing_list'"
        class="form_item"
        :class="{ had_input: link_infringing_list[index] }"
      >
        <input type="text" v-model="link_infringing_list[index]" maxlength="1000" />
        <label for="">Links to the allegedly infringing work</label>
      </div>
      <a
        href="javascript:;"
        style="display: block; margin-bottom: 24px; color: #5f4ae2"
        @click="add_link('link_infringing_list')"
        >+ Add another link</a
      >
      <label for="">
        Description of the specific material (or portion thereof) that is
        claimed to be infringing or be the subject of infringing activity and
        that is to be removed or access to which is to be disabled
      </label>
      <div
        class="form_item"
        :class="{ had_input: payload.infringing_description }"
      >
        <textarea
          v-model="payload.infringing_description"
          name="infringing_description"
          maxlength="1000"
          id="infringing_description"
          @blur="required"
        ></textarea>
        <div class="error" :class="{ required: error.infringing_description }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <h3>Identify your work</h3>
      <p>
        Please provide link(s) to the content you believe violates or infringes
        your copyright on the store. Ensure you have provided a comprehensive
        list to each individual page with the content you would like removed and
        not just the main shop URL. Up to 100 links may be included in the same
        notice.
      </p>
      <label for="">Links to the original work</label>
      <div
        class="form_item"
        :class="{ had_input: payload.link_to_original_work }"
      >
        <input
          type="text"
          maxlength="1000"
          name="link_to_original_work"
          id="link_to_original_work"
          @blur="required"
          v-model="payload.link_to_original_work"
        />
        <label for="">Links to the original work</label>
        <div class="error" :class="{ required: error.link_to_original_work }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <div
        v-for="(item, index) in link_original_list"
        :key="index + 'link_original_list'"
        class="form_item"
        :class="{ had_input: link_original_list[index] }"
      >

        <input type="text" v-model="link_original_list[index]" maxlength="1000" />
        <label for="">Links to the original work</label>
      </div>
      <a
        href="javascript:;"
        style="display: block; margin-bottom: 24px; color: #5f4ae2"
        @click="add_link('link_original_list')"
        >+ Add another link</a
      >
      <label for="">Description of the original work</label>
      <div
        class="form_item"
        :class="{ had_input: payload.original_work_description }"
      >
        <textarea
          v-model="payload.original_work_description"
          name="original_work_description"
          maxlength="1000"
          id="original_work_description"
          @blur="required"
        ></textarea>
        <div
          class="error"
          :class="{ required: error.original_work_description }"
        >
          Can't be blank, please fill out this field
        </div>
      </div>
      <p>
        Please provide a description of the original work (e.g. photograph I
        took, product description or blog post I wrote, video I made, etc.)
      </p>
      <label for="">Evidence of ownership of work/trademark</label>
      <div class="form_item" :class="{ had_input: payload.proof_of_ownership }">
        <textarea
          v-model="payload.proof_of_ownership"
          maxlength="1000"
          name="proof_of_ownership"
          id="proof_of_ownership"
          @blur="required"
        ></textarea>
        <div class="error" :class="{ required: error.proof_of_ownership }">
          Can't be blank, please fill out this field
        </div>
      </div>
      <p>
        Please provide the evidence of ownership and the description of the
        factual and/or legal reasons why you believe that the campaign violates
        your rights
      </p>
    </div>
    <div class="form_info">
      <h3>Statements</h3>
      <div class="form_check">
        <label for="authorized"
          ><input
            type="checkbox"
            name="authorized"
            id="authorized"
            v-model="payload.authorized"
          />
          <span class="check" :class="{ active: payload.authorized }">√</span>
          I have a good faith belief that use of the material in the manner
          complained of is authorized by the copyright owner, its agent, or the
          law.
        </label>
        <div
          class="error"
          :class="{ required: error.authorized && !payload.authorized }"
        >
          This field is required
        </div>
      </div>
      <div class="form_check">
        <label for="disclaims"
          ><input
            type="checkbox"
            name="disclaims"
            id="disclaims"
            v-model="payload.disclaims"
          />
          <span class="check" :class="{ active: payload.disclaims }">√</span>
          I swear, under penalty of perjury, that the information in the
          notification is accurate, and that I am the copyright owner or am
          authorized to act on behalf of the owner of an exclusive right that is
          allegedly infringed.
        </label>
        <div
          class="error"
          :class="{ required: error.disclaims && !payload.disclaims }"
        >
          This field is required
        </div>
      </div>
      <div class="form_check">
        <label for="provision"
          ><input
            type="checkbox"
            name="provision"
            id="provision"
            v-model="payload.provision"
          />
          <span class="check" :class="{ active: payload.provision }">√</span>
          I acknowledge that a copy of this infringement notice, including any
          contact information provided above, may be provided to the person that
          posted the content being reported.
        </label>
        <div
          class="error"
          :class="{ required: error.provision && !payload.provision }"
        >
          This field is required
        </div>
      </div>
    </div>
    <div class="form_info">
      <h3>Statements</h3>
      <label for=""
        >Typing your name in the box below will act as your electronic
        signature.</label
      >
      <div
        class="form_item"
        :class="{ had_input: payload.electronic_signature }"
      >
        <input
          type="text"
          name="electronic_signature"
          id="electronic_signature"
          @blur="required"
          v-model="payload.electronic_signature"
        />
        <label for="">Your name</label>
        <div class="error" :class="{ required: error.electronic_signature }">
          Can't be blank, please fill out this field
        </div>
      </div>
    </div>
    <div class="send">
      <button
        class="main_button"
        :class="{ button_active: button_active }"
        :disabled="button_active"
        @click="send"
      >
        SEND
      </button>
    </div>
  </div>
</template>
<script>
// import API from "../../API";
export default {
  data() {
    return {
      payload: {
        name: "",
        email: "",
        address: "",
        phone: "",
        company: "",
        infringing_address: "",
        infringing_description: "",
        link_to_original_work: "",
        original_work_description: "",
        proof_of_ownership: "",
        copyright_owner: true,
        authorized: false,
        disclaims: false,
        provision: false,
        electronic_signature: "",
      },
      reset_payload: {
        name: "",
        email: "",
        address: "",
        phone: "",
        company: "",
        infringing_address: "",
        infringing_description: "",
        link_to_original_work: "",
        original_work_description: "",
        proof_of_ownership: "",
        copyright_owner: true,
        electronic_signature: "",
      },
      error: {},
      link_infringing_list: [],
      link_original_list: [],
      button_active: false,
    };
  },
  methods: {
    foucus_id(id) {
      let el = this.$("#" + id);
      let top = el.offset().top;
      this.$("html,body").animate({ scrollTop: top - 50 }, 200);
      el.focus();
    },
    add_link(link) {
      if (this[link].length < 99) {
        this[link].push("");
      }
    },
    required(e) {
      let reg = /^([0-9A-Za-z\-_\.]+)@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
      console.log(e.target.value.length);
      if (
        !e.target.value ||
        e.target.value.length > 1000 ||
        (e.target.name == "email" && !reg.test(e.target.value))
      ) {
        this.$set(this.error, e.target.name, true);
      } else {
        this.$set(this.error, e.target.name, false);
      }
    },
    text_required() {
      let value = true;
      for (const key in this.payload) {
        let reg = /^([0-9A-Za-z\-_\.]+)@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
        if (
          (!this.payload[key] &&
            key != "company" &&
            key != "copyright_owner") ||
          (key == "email" && !reg.test(this.payload[key]))
        ) {
          this.$set(this.error, key, true);
          if (value) {
            this.foucus_id(key);
          }
          value = false;
        } else {
          this.$set(this.error, key, false);
        }
      }
      return value;
    },
    async send() {
      let onOff = this.text_required();
      if (onOff) {
        this.button_active = true;
        try {
          let infringing_address = [
            this.payload.infringing_address,
            ...this.link_infringing_list,
          ];
          let link_to_original_work = [
            this.payload.link_to_original_work,
            ...this.link_original_list,
          ];
          let payload = {
            ...this.payload,
            infringing_address,
            link_to_original_work,
          };
          await this.$axios.post(API.report, payload);
          this.button_active = false;
          this.$(".success").show();
          this.$("html,body").animate({ scrollTop: 0 }, 500);
          this.payload = { ...this.payload, ...this.reset_payload };
          this.link_infringing_list = [];
          this.link_original_list = [];
        } catch (error) {
          this.button_active = false;
          if (
            error?.response?.data?.message
          ) {
            this.$message.error(error.response.data.message);
          }
        }
      }
    },
  },
};
</script>
<style scoped lang='scss'>
.dmca_form {
  h2,
  h3 {
    margin: 24px 0;
    padding: 0;
  }
  p {
    padding: 0;
    margin-bottom: 12px;
  }
  .form_info {
    & > label {
      margin-bottom: 12px;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      font-size: 16px;
      font-style: normal;
      line-height: 1.5;
      color: #333;
      text-align: left;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      box-sizing: border-box;
      display: inline-block;
      font-weight: bold;
    }
    .form_item {
      position: relative;
      padding-bottom: 16px;
      textarea {
        padding-left: 16px;
        padding-right: 16px;
        margin-bottom: 12px;
        font-size: 21px;
        -webkit-font-smoothing: antialiased;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        box-sizing: border-box;
        line-height: inherit;
        background: #fff;
        min-height: 40px;
        box-shadow: none;
        padding-top: 8px;
        overflow: auto;
        resize: vertical;
        width: 100%;
        outline: none;
        border: 1px solid rgba(51, 51, 51, 0.8);
      }
      input {
        margin-bottom: 12px;
        padding: 0 16px;
        -webkit-font-smoothing: antialiased;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        box-sizing: border-box;
        min-height: 40px;
        outline: none;
        box-shadow: none;
        font-weight: 700;
        font-style: normal;
        line-height: normal;
        color: #000;
        width: 100%;
        border: none;
        border-bottom-width: 1px;
        border-bottom-style: solid;
        text-transform: none;
        font-size: 21px;
        background-color: initial;
        &:focus {
          border: 1px solid #20c997;
          & ~ label {
            top: -16px;
            font-size: 14px;
            color: #20c997;
          }
        }
      }
      label {
        color: #999;
        position: absolute;
        pointer-events: none;
        user-select: none;
        left: 0;
        top: 10px;
        font-size: 16px;
        font-weight: 600;
        transition: 0.2s ease all;
        -moz-transition: 0.2s ease all;
        -webkit-transition: 0.2s ease all;
      }
      &:hover {
        input {
          border: 1px solid #20c997;
        }
      }
    }
    .form_item.had_input {
      input {
        border: 1px solid #20c997;
        & ~ label {
          top: -16px;
          font-size: 14px;
          color: #20c997;
        }
      }
    }
    .form_radio,
    .form_check {
      label {
        display: block;
        cursor: pointer;
        input {
          width: 1px;
          height: 1px;
          visibility: hidden;
          display: inline-block;
        }
        .check {
          position: relative;
          display: inline-block;
          width: 24px;
          height: 24px;
          line-height: 24px;
          vertical-align: middle;
          border: 1px solid #bfbfbf;
          background-color: white;
          margin-right: 5px;
          text-align: center;
          font-weight: 600;
          color: white;
          &:hover {
            border-color: black;
          }
        }
        .check.active {
          background-color: #bfbfbf;
        }
        .dot {
          position: relative;
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 1px solid #999999;
          vertical-align: middle;
          margin-right: 5px;
          padding: 2px;
        }
        .dot.active {
          &::before {
            content: "";
            position: absolute;
            display: inline-block;
            top: 2px;
            left: 2px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #7f7f7f;
          }
        }
      }
    }
  }
  .send {
    overflow: hidden;
    margin-bottom: 20px;
    button {
      float: right;
      outline: none;
      border: none;
      background-color: #5f4ae2;
      color: white;
      text-transform: uppercase;
      font-size: 14px;
      line-height: normal;
      letter-spacing: 0.5px;
      padding: 11px 22px;
    }
  }
  .error {
    color: #dc3545;
    font-size: 12px;
    display: none;
  }
  .error.required {
    display: block;
  }
  .success {
    display: none;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    font-size: 16px;
    font-weight: 400;
    font-style: normal;
    line-height: 1.5;
    text-align: left;
    -webkit-tap-highlight-color: transparent;
    box-sizing: border-box;
    background: #e0f4d8;
    color: #1f9108;
    padding: 24px;
  }
}
</style>
